/**
 * usePacmanGame - Main hook for Pac-Man mini-game logic
 * Manages game state, animations, and collision detection
 */

import { useReducer, useCallback, useEffect, useRef } from 'react';
import type {
  PacmanGameState,
  PacmanAction,
  Maze,
  GhostState,
  PacmanState,
  GridPosition,
  PixelPosition,
} from '../types/pacman';
import {
  PACMAN_SPEED,
  GHOST_SPEED,
  TILE_SIZE,
  CHASE_MAX_DURATION,
  COLLISION_THRESHOLD,
} from '../types/pacman';
import { useMazeGenerator } from './useMazeGenerator';
import { usePathfinding } from './usePathfinding';

// Initial state
const initialState: PacmanGameState = {
  phase: 'IDLE',
  maze: null,
  pacman: null,
  ghosts: [],
  capturedMemberId: null,
  chaseStartTime: null,
  captureOrder: [],
  isLastGhost: false,
  fireworksTriggered: false,
};

// Reducer
function pacmanReducer(state: PacmanGameState, action: PacmanAction): PacmanGameState {
  switch (action.type) {
    case 'INIT_GAME': {
      const ghosts: GhostState[] = action.memberIds.map((memberId, i) => {
        const spawn = action.maze.ghostSpawns[i];
        return {
          id: `ghost-${i}`,
          memberId,
          position: gridToPixel(spawn.position),
          gridPosition: spawn.position,
          axis: spawn.axis,
          velocity: Math.random() > 0.5 ? 1 : -1,
          isCaptured: false,
        };
      });

      return {
        ...initialState,
        phase: 'IDLE',
        maze: action.maze,
        pacman: {
          position: gridToPixel(action.maze.pacmanSpawn),
          gridPosition: action.maze.pacmanSpawn,
          direction: 'RIGHT',
          mouthOpen: true,
        },
        ghosts,
      };
    }

    case 'START_CHASE':
      return { ...state, phase: 'CHASE', chaseStartTime: Date.now() };

    case 'UPDATE_POSITIONS':
      return { ...state, pacman: action.pacman, ghosts: action.ghosts };

    case 'CAPTURE_GHOST': {
      const ghost = state.ghosts.find(g => g.id === action.ghostId);
      if (!ghost) return state;

      const remaining = state.ghosts.filter(g => !g.isCaptured && g.id !== action.ghostId);

      return {
        ...state,
        phase: 'CAPTURE',
        capturedMemberId: ghost.memberId,
        captureOrder: [...state.captureOrder, ghost.memberId],
        isLastGhost: remaining.length === 0,
        ghosts: state.ghosts.map(g =>
          g.id === action.ghostId ? { ...g, isCaptured: true } : g
        ),
      };
    }

    case 'SHOW_POPUP':
      return { ...state, phase: 'POPUP' };

    case 'GO_TO_KANBAN':
      return { ...state, phase: 'TRANSITION' };

    case 'RETURN_TO_MAZE':
      return {
        ...state,
        phase: 'IDLE',
        capturedMemberId: null,
        chaseStartTime: null,
        pacman: state.maze ? {
          ...state.pacman!,
          position: gridToPixel(state.maze.pacmanSpawn),
          gridPosition: state.maze.pacmanSpawn,
        } : state.pacman,
      };

    case 'TRIGGER_FIREWORKS':
      return { ...state, fireworksTriggered: true };

    case 'SYNC_CAPTURED_GHOSTS': {
      // Mark ghosts as captured based on global capture order
      const capturedIds = new Set(action.capturedMemberIds);
      const updatedGhosts = state.ghosts.map(ghost => ({
        ...ghost,
        isCaptured: capturedIds.has(ghost.memberId),
      }));
      const remaining = updatedGhosts.filter(g => !g.isCaptured);
      return {
        ...state,
        ghosts: updatedGhosts,
        captureOrder: action.capturedMemberIds,
        isLastGhost: remaining.length === 1,
      };
    }

    default:
      return state;
  }
}

// Hook
export function usePacmanGame(memberIds: string[]) {
  const [state, dispatch] = useReducer(pacmanReducer, initialState);
  const { generateMaze } = useMazeGenerator();
  const { findPath } = usePathfinding();
  const frameRef = useRef<number>(0);
  const mouthToggleRef = useRef<number>(0);

  // Initialize game with a maze (either provided or generated)
  const initGame = useCallback((existingMaze?: Maze | null) => {
    const maze = existingMaze || generateMaze(memberIds.length);
    dispatch({ type: 'INIT_GAME', maze, memberIds });
    return maze;
  }, [memberIds, generateMaze]);

  // Game loop
  useEffect(() => {
    if (state.phase !== 'CHASE' || !state.maze || !state.pacman) return;

    const loop = () => {
      const { maze, pacman, ghosts, chaseStartTime } = state;
      if (!maze || !pacman) return;

      // 1. Update ghosts (ping-pong movement)
      const updatedGhosts = ghosts.map(ghost => {
        if (ghost.isCaptured) return ghost;
        return updateGhostPingPong(ghost, maze);
      });

      // 2. Update Pac-Man (chase nearest ghost)
      const activeGhosts = updatedGhosts.filter(g => !g.isCaptured);
      let updatedPacman = pacman;

      if (activeGhosts.length > 0) {
        const target = findClosestGhost(pacman, activeGhosts);
        if (target) {
          const path = findPath(pacman.gridPosition, target.gridPosition, maze);
          if (path.length > 1) {
            updatedPacman = movePacmanToward(pacman, path[1]);
          }
        }
      }

      // Toggle mouth animation
      mouthToggleRef.current++;
      if (mouthToggleRef.current % 8 === 0) {
        updatedPacman = { ...updatedPacman, mouthOpen: !updatedPacman.mouthOpen };
      }

      // 3. Check collision
      const captured = detectCollision(updatedPacman, updatedGhosts);
      if (captured) {
        dispatch({ type: 'CAPTURE_GHOST', ghostId: captured.id });
        return;
      }

      // 4. Check timeout (3 seconds)
      if (chaseStartTime && Date.now() - chaseStartTime > CHASE_MAX_DURATION) {
        const nearest = findClosestGhost(updatedPacman, activeGhosts);
        if (nearest) {
          dispatch({ type: 'CAPTURE_GHOST', ghostId: nearest.id });
          return;
        }
      }

      // 5. Update state
      dispatch({ type: 'UPDATE_POSITIONS', pacman: updatedPacman, ghosts: updatedGhosts });

      frameRef.current = requestAnimationFrame(loop);
    };

    frameRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(frameRef.current);
  }, [state.phase, state.maze, state.pacman, state.ghosts, state.chaseStartTime, findPath]);

  // Transition to popup after capture
  useEffect(() => {
    if (state.phase === 'CAPTURE') {
      const timer = setTimeout(() => dispatch({ type: 'SHOW_POPUP' }), 200);
      return () => clearTimeout(timer);
    }
  }, [state.phase]);

  // Sync captured ghosts with global state
  const syncCapturedGhosts = useCallback((capturedMemberIds: string[]) => {
    dispatch({ type: 'SYNC_CAPTURED_GHOSTS', capturedMemberIds });
  }, []);

  return {
    state,
    initGame,
    startChase: () => dispatch({ type: 'START_CHASE' }),
    goToKanban: () => dispatch({ type: 'GO_TO_KANBAN' }),
    returnToMaze: () => dispatch({ type: 'RETURN_TO_MAZE' }),
    triggerFireworks: () => dispatch({ type: 'TRIGGER_FIREWORKS' }),
    syncCapturedGhosts,
  };
}

// Utility functions
function gridToPixel(grid: GridPosition): PixelPosition {
  return {
    x: grid.x * TILE_SIZE + TILE_SIZE / 2,
    y: grid.y * TILE_SIZE + TILE_SIZE / 2,
  };
}

function pixelToGrid(pixel: PixelPosition): GridPosition {
  return {
    x: Math.floor(pixel.x / TILE_SIZE),
    y: Math.floor(pixel.y / TILE_SIZE),
  };
}

function updateGhostPingPong(ghost: GhostState, maze: Maze): GhostState {
  let newX = ghost.position.x;
  let newY = ghost.position.y;
  let newVelocity = ghost.velocity;

  if (ghost.axis === 'HORIZONTAL') {
    newX += GHOST_SPEED * ghost.velocity;
    const nextGridX = Math.floor((newX + (ghost.velocity > 0 ? TILE_SIZE / 2 : -TILE_SIZE / 2)) / TILE_SIZE);

    if (maze.cells[ghost.gridPosition.y]?.[nextGridX]?.type === 'WALL') {
      newVelocity = -ghost.velocity;
      newX = ghost.position.x;
    }
  } else {
    newY += GHOST_SPEED * ghost.velocity;
    const nextGridY = Math.floor((newY + (ghost.velocity > 0 ? TILE_SIZE / 2 : -TILE_SIZE / 2)) / TILE_SIZE);

    if (maze.cells[nextGridY]?.[ghost.gridPosition.x]?.type === 'WALL') {
      newVelocity = -ghost.velocity;
      newY = ghost.position.y;
    }
  }

  return {
    ...ghost,
    position: { x: newX, y: newY },
    gridPosition: pixelToGrid({ x: newX, y: newY }),
    velocity: newVelocity,
  };
}

function findClosestGhost(pacman: PacmanState, ghosts: GhostState[]): GhostState | null {
  const active = ghosts.filter(g => !g.isCaptured);
  if (active.length === 0) return null;

  return active.reduce((closest, ghost) => {
    const distCurrent = Math.abs(ghost.gridPosition.x - pacman.gridPosition.x) +
                        Math.abs(ghost.gridPosition.y - pacman.gridPosition.y);
    const distClosest = Math.abs(closest.gridPosition.x - pacman.gridPosition.x) +
                        Math.abs(closest.gridPosition.y - pacman.gridPosition.y);
    return distCurrent < distClosest ? ghost : closest;
  });
}

function movePacmanToward(pacman: PacmanState, target: GridPosition): PacmanState {
  const dx = target.x - pacman.gridPosition.x;
  const dy = target.y - pacman.gridPosition.y;

  let newX = pacman.position.x;
  let newY = pacman.position.y;
  let direction = pacman.direction;

  if (dx !== 0) {
    newX += Math.sign(dx) * PACMAN_SPEED;
    direction = dx > 0 ? 'RIGHT' : 'LEFT';
  } else if (dy !== 0) {
    newY += Math.sign(dy) * PACMAN_SPEED;
    direction = dy > 0 ? 'DOWN' : 'UP';
  }

  return {
    ...pacman,
    position: { x: newX, y: newY },
    gridPosition: pixelToGrid({ x: newX, y: newY }),
    direction,
  };
}

function detectCollision(pacman: PacmanState, ghosts: GhostState[]): GhostState | null {
  for (const ghost of ghosts) {
    if (ghost.isCaptured) continue;
    const dx = Math.abs(pacman.position.x - ghost.position.x);
    const dy = Math.abs(pacman.position.y - ghost.position.y);
    if (dx < COLLISION_THRESHOLD && dy < COLLISION_THRESHOLD) {
      return ghost;
    }
  }
  return null;
}
